import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../services/api';
import { Lock, ArrowRight, Buildings, Warning } from '@phosphor-icons/react';

export default function ForcePasswordChange() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError('Die neuen Passwörter stimmen nicht überein');
      return;
    }
    if (newPassword.length < 8) {
      setError('Das neue Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    setLoading(true);
    try {
      await changePassword({ current_password: currentPassword, new_password: newPassword });
      await checkAuth();
      navigate(user?.role === 'super_admin' ? '/super-admin' : '/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Passwort-Wechsel');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50" data-testid="force-password-change-page">
      <div className="w-full max-w-md bg-white border border-gray-200 p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-6">
          <Buildings weight="bold" className="w-7 h-7 text-slate-900" />
          <span className="font-heading text-lg font-black tracking-tight">IMMO VERWALTER</span>
        </div>

        <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 mb-6 flex items-start gap-3" data-testid="force-warning">
          <Warning weight="bold" className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Passwort-Wechsel erforderlich</p>
            <p className="text-sm mt-1">Sie nutzen ein automatisch generiertes Passwort. Bitte setzen Sie aus Sicherheitsgründen jetzt ein eigenes Passwort.</p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 p-3 text-sm" data-testid="force-error">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Aktuelles Passwort (generiert)</label>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full border-b border-slate-300 bg-transparent pl-8 py-3 focus:border-slate-900 outline-none"
                required
                data-testid="force-current-password"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Neues Passwort (mind. 8 Zeichen)</label>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border-b border-slate-300 bg-transparent pl-8 py-3 focus:border-slate-900 outline-none"
                required
                minLength={8}
                data-testid="force-new-password"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Neues Passwort bestätigen</label>
            <div className="relative">
              <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full border-b border-slate-300 bg-transparent pl-8 py-3 focus:border-slate-900 outline-none"
                required
                minLength={8}
                data-testid="force-confirm-password"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white py-3 font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="force-submit-button"
          >
            {loading ? 'Wird gespeichert...' : 'Passwort setzen'}
            <ArrowRight weight="bold" className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}
